import React, { useState } from 'react';
import { Box, Typography, IconButton, Tooltip } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useNavigate } from 'react-router-dom';

import Table from '../../../../components/tables/Table';
import tableStyles from '../../../../components/tables/Table.styles';

import OperatorActionsDialog from '../Edit/OperatorActionsDialog';

const COLUMNS = [
  {
    label: 'Operator',
    field: 'name',
    type: 'string',
    sortable: true,
    render: (_row, v, navigateToUnits) => (
      <Typography
        sx={tableStyles.tableLink}
        onClick={() => navigateToUnits(String(v))}
      >
        {v}
      </Typography>
    ),
  },
  {
    label: 'Code',
    field: 'description',
    type: 'string',
    sortable: true,
  },
  {
    label: (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        Units
        <Tooltip title="Number of active trains today" arrow>
          <InfoOutlinedIcon
            fontSize="small"
            sx={{ color: '#5236AB', verticalAlign: 'middle', m: 0 }}
          />
        </Tooltip>
      </Box>
    ),
    field: 'unitCount',
    type: 'number',
    sortable: true,
    render: (_row, v) => v ?? 0,
  },
  {
    label: (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        EVNs
        <Tooltip title="Number of active vehicles today" arrow>
          <InfoOutlinedIcon
            fontSize="small"
            sx={{ color: '#5236AB', verticalAlign: 'middle', m: 0 }}
          />
        </Tooltip>
      </Box>
    ),
    field: 'evnCount',
    type: 'number',
    sortable: true,
    render: (_row, v) => v ?? 0,
  },
  {
    label: (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        Meters
        <Tooltip title="Number of active meters today" arrow>
          <InfoOutlinedIcon
            fontSize="small"
            sx={{ color: '#5236AB', verticalAlign: 'middle', m: 0 }}
          />
        </Tooltip>
      </Box>
    ),
    field: 'meterCount',
    type: 'number',
    sortable: true,
    render: (_row, v) => v ?? 0,
  },
];

export default function OperatorTable({ rows = [], refreshing }) {
  const navigate = useNavigate();

  const [selectedRow, setSelectedRow] = useState(null);
  const [actionsOpen, setActionsOpen] = useState(false);

  const navigateToUnits = (name) =>
    navigate(`/home/units?operator=${encodeURIComponent(name)}`);

  const actionsRenderer = (row) => (
    <div style={{ display: 'flex', gap: 8 }}>
      <IconButton
        aria-label="Edit"
        size="small"
        sx={tableStyles.compactActionButton}
        onClick={() => {
          setSelectedRow(row);
          setActionsOpen(true);
        }}
      >
        <EditIcon sx={tableStyles.editIcon} />
      </IconButton>
    </div>
  );

  const columnsWithNav = COLUMNS.map((c) =>
    c.render
      ? {
          ...c,
          render: (row, value) =>
            c.render(row, value ?? row?.[c.field], navigateToUnits),
        }
      : c
  );

  return (
    <>
      <Table
        rows={Array.isArray(rows) ? rows : []}
        columns={columnsWithNav}
        actionsRenderer={actionsRenderer}
        actionsHeader="Actions"
        stickyHeader
        zebra
        hover
        bordered
        emptyMessage={refreshing ? 'Loading…' : '—'}
      />

      {actionsOpen && selectedRow && (
        <OperatorActionsDialog
          open
          onClose={() => {
            setActionsOpen(false);
            setSelectedRow(null);
          }}
          initialData={{
            code: selectedRow.description,
            name: selectedRow.name,
            operator: selectedRow.name,
          }}
          onSubmit={() => {
            setActionsOpen(false);
            setSelectedRow(null);
          }}
        />
      )}
    </>
  );
}
